import { useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import useTranslation from '@/hooks/useTranslation';
import { useMutateReRunAnalyze } from '@/reactQuery/post';
import { useInvalidateAnalysisSchemaStatus } from '@/reactQuery/researchAssistant';

/**
 * Reason codes returned by the backend /analysis-schema-status endpoint.
 * - `no_analysis`: project has never been analysed
 * - `old_schema`: project was analysed before the RA rework and needs
 *   re-analysis to produce the new overall_pages fields (voice_quotes,
 *   divergent_views_and_outliers, sample_profile, etc.)
 */
export type OutdatedAnalysisReason = 'no_analysis' | 'old_schema';

interface OutdatedAnalysisDialogProps {
  isOpen: boolean;
  /** Called when the user clicks Cancel or closes the dialog. */
  onCancel: () => void;
  /** Called after a successful re-run so the parent can refetch / re-enable the feature. */
  onReRunComplete: () => void;
  projectId: string | undefined;
  /** Null for project-level re-run; set for filter-scoped re-run. */
  filterId?: string | null;
  reason: OutdatedAnalysisReason;
  /**
   * Which feature triggered the dialog. Affects the copy:
   * - `research_assistant`: "This project's analysis needs to be updated
   *   to use the Research Assistant."
   * - `compare`: "Compare needs an up-to-date analysis..."
   */
  feature: 'research_assistant' | 'compare';
}

/**
 * Modal shown when the user tries to open Research Assistant or Compare
 * on a project whose analysis predates the rework (or never ran at all).
 *
 * Confirming the dialog calls POST /analyzer/run/:project_id?force=true
 * to kick off a fresh analysis that writes the new schema fields. On
 * success we invalidate the schema-status cache so the parent component
 * sees `current: true` on its next read and unlocks the feature.
 *
 * The dialog is intentionally modal (not dismissable by clicking outside)
 * while the re-run is in flight — closing it mid-run would leave the user
 * in a confusing state with the re-run still happening in the background.
 */
function OutdatedAnalysisDialog({
  isOpen,
  onCancel,
  onReRunComplete,
  projectId,
  filterId,
  reason,
  feature,
}: OutdatedAnalysisDialogProps) {
  const { lang } = useTranslation();
  const [isStarting, setIsStarting] = useState(false);
  const { mutateAsync: mutateAsyncReRun } = useMutateReRunAnalyze();
  const invalidateSchemaStatus = useInvalidateAnalysisSchemaStatus();

  const title =
    reason === 'no_analysis'
      ? lang.get('msg.analysisNeededTitle')
      : lang.get('msg.analysisOutdatedTitle');

  const description =
    reason === 'no_analysis'
      ? feature === 'research_assistant'
        ? lang.get('msg.analysisNeededForResearchAssistant')
        : lang.get('msg.analysisNeededForCompare')
      : feature === 'research_assistant'
        ? lang.get('msg.analysisOutdatedForResearchAssistant')
        : lang.get('msg.analysisOutdatedForCompare');

  const handleConfirm = async () => {
    if (!projectId) return;
    setIsStarting(true);
    try {
      // force=true bypasses the "no new conversations" early-return so
      // a fully-analysed (but pre-rework) project gets re-processed
      // through the current pipeline and writes the new schema fields.
      await mutateAsyncReRun({
        project_id: projectId,
        f_id: filterId || null,
        force: true,
      });
      // Success — the run has STARTED (not finished). The parent
      // component (ProjectResults) already renders its own "analysis
      // running" indicator via useGetRunState, so we hand off to that
      // UI. Invalidate the schema-status cache so the dialog stays
      // dismissed on subsequent mounts.
      invalidateSchemaStatus(projectId);
      onReRunComplete();
      toast.success(lang.get('msg.reRunStarted'));
    } catch (err: unknown) {
      // Re-run kickoff failed — the backend already has the run state
      // locked or there's a different error. Surface it and leave the
      // dialog open so the user can retry.
      const axiosError = err as { message?: string };
      toast.error(axiosError?.message || lang.get('msg.reRunFailed'));
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent className="sm:max-w-[480px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-dark-text leading-relaxed">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="mt-5">
          <AlertDialogCancel onClick={onCancel} disabled={isStarting}>
            {lang.get('msg.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isStarting || !projectId}
            className="gap-2"
          >
            {isStarting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {lang.get('msg.startingReRun')}
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                {lang.get('msg.reRunAnalysis')}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default OutdatedAnalysisDialog;
